// Shared presentational sub-components for the Maintenance / Tasks module.
// Extracted from Maintenance.jsx (P3.c) so the page, JobDetailModal and the
// RoutineTab family can all import them without circular dependencies. These
// are leaf components — no dependency on the big modals.

import { useState, useMemo, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Plus, Trash2, X, ImagePlus, Clock, CheckCircle2, BadgeCheck, MessageSquare, Receipt, Wrench, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { toast } from "sonner";
import { fmtErr } from "../../../lib/api";
import FileUploader, { AuthImage, VideoThumb } from "../../../components/FileUploader";
import { isVideoPath } from "../../../lib/videoCompress";
import ImageLightbox from "../../../components/ImageLightbox";
import PhotoMarkup from "../../../components/PhotoMarkup";
import { Field } from "../Inventory";
import { SYSTEMS } from "./constants";

// Compact quote-progress chip for the Tasks list. Reads task.quote_summary
// ({total, invited, quoted, accepted}) and shows accepted > received > awaiting.
// When `onClick` is provided it renders as a button (jumps to the quotes tab).
export function QuoteSummaryBadge({ summary, onClick }) {
  if (!summary || !summary.total) return null;
  const { quoted = 0, accepted = 0, invited = 0, total = 0 } = summary;
  let cfg;
  if (accepted > 0) {
    cfg = { Icon: BadgeCheck, text: "Quote accepted", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  } else if (quoted > 0) {
    cfg = { Icon: CheckCircle2, text: `${quoted} quote${quoted > 1 ? "s" : ""} received`, cls: "bg-sky-50 text-sky-700 border-sky-200" };
  } else {
    cfg = { Icon: Clock, text: `Awaiting ${invited || total} quote${(invited || total) > 1 ? "s" : ""}`, cls: "bg-amber-50 text-amber-700 border-amber-200" };
  }
  const { Icon } = cfg;
  const cls = `inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium whitespace-nowrap ${cfg.cls}`;
  const title = `${total} supplier(s) requested · ${quoted} quoted · ${accepted} accepted — view quotes`;
  const inner = <><Icon className="h-3 w-3" />{cfg.text}</>;
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${cls} cursor-pointer hover:brightness-95 transition`} data-testid="quote-summary-badge" title={title}>
        {inner}
      </button>
    );
  }
  return (
    <span className={cls} data-testid="quote-summary-badge" title={title}>{inner}</span>
  );
}


// Small red "N new" chip shown next to the quote badge in the Tasks list when
// a supplier has posted messages the crew hasn't read. Click jumps to quotes.
export function MessagesBadge({ count, onClick }) {
  if (!count) return null;
  const label = `${count} new message${count > 1 ? "s" : ""}`;
  const cls = "inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 text-red-700 px-2 py-0.5 text-[10px] font-medium whitespace-nowrap";
  const inner = <><MessageSquare className="h-3 w-3" />{label}</>;
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${cls} cursor-pointer hover:brightness-95 transition`} data-testid="messages-badge" title={label}>
        {inner}
      </button>
    );
  }
  return <span className={cls} data-testid="messages-badge" title={label}>{inner}</span>;
}


// Shared System picker. Renders the canonical SYSTEMS list, merges in any
// systems already used on this yacht (so a custom one typed earlier
// reappears), and offers a "+ Add new system…" sentinel that flips the
// control into a free-text input. `system_category` is plain text on the
// backend, so any typed value persists on the record — the list is fully
// extendable without a schema change.
export function SystemSelect({ value, onChange, used = [], placeholder, testid }) {
  const ADD = "__add_new_system__";
  const [custom, setCustom] = useState(false);
  const options = useMemo(() => {
    const out = [];
    const seen = new Set();
    [...SYSTEMS, ...used, value].forEach((s) => {
      const v = (s || "").trim();
      if (v && !seen.has(v)) { seen.add(v); out.push(v); }
    });
    return out;
  }, [used, value]);

  if (custom) {
    return (
      <div className="flex gap-2">
        <Input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="New category name"
          data-testid={testid ? `${testid}-custom` : undefined}
        />
        <Button
          type="button"
          variant="outline"
          className="h-9 shrink-0"
          onClick={() => setCustom(false)}
          data-testid={testid ? `${testid}-custom-back` : undefined}
        >
          List
        </Button>
      </div>
    );
  }
  return (
    <select
      className="h-9 w-full rounded-md border border-input px-3 text-sm"
      value={value}
      onChange={(e) => {
        if (e.target.value === ADD) { onChange(""); setCustom(true); }
        else onChange(e.target.value);
      }}
      data-testid={testid}
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((s) => <option key={s} value={s}>{s}</option>)}
      <option value={ADD}>+ Add new category…</option>
    </select>
  );
}

// Multi-select variant of SystemSelect — a task can belong to several
// categories. Stores an array of plain-text category names; the backend keeps
// system_categories[0] as the primary `system_category` for legacy reads.
export function MultiSystemSelect({ value = [], onChange, used = [], testid }) {
  const ADD = "__add_new_system__";
  const [custom, setCustom] = useState(false);
  const [draft, setDraft] = useState("");
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const options = useMemo(() => {
    const out = []; const seen = new Set();
    [...SYSTEMS, ...used, ...selected].forEach((s) => {
      const v = (s || "").trim();
      if (v && !seen.has(v)) { seen.add(v); out.push(v); }
    });
    // Alphabetical for quick scanning.
    return out.sort((a, b) => a.localeCompare(b));
  }, [used, selected]);
  const add = (v) => {
    const t = (v || "").trim();
    if (t && !selected.includes(t)) onChange([...selected, t]);
  };
  const remove = (v) => onChange(selected.filter((s) => s !== v));
  const commitCustom = () => { add(draft); setDraft(""); setCustom(false); };
  return (
    <div className="space-y-2" data-testid={testid}>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((s) => (
            <span key={s} className="inline-flex items-center gap-1 rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs" data-testid={testid ? `${testid}-chip-${s}` : undefined}>
              {s}
              <button type="button" className="text-zinc-400 hover:text-red-500" onClick={() => remove(s)} data-testid={testid ? `${testid}-remove-${s}` : undefined}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {custom ? (
        <div className="flex gap-2">
          <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitCustom(); } }}
            placeholder="New category name" data-testid={testid ? `${testid}-custom` : undefined} />
          <Button type="button" variant="outline" className="h-9 shrink-0" onClick={commitCustom} data-testid={testid ? `${testid}-custom-add` : undefined}>Add</Button>
          <Button type="button" variant="ghost" className="h-9 shrink-0" onClick={() => { setCustom(false); setDraft(""); }}>Cancel</Button>
        </div>
      ) : (
        <select className="h-9 w-full rounded-md border border-input px-3 text-sm" value=""
          onChange={(e) => { if (e.target.value === ADD) setCustom(true); else add(e.target.value); }}
          data-testid={testid}>
          <option value="">Add category…</option>
          {options.filter((o) => !selected.includes(o)).map((s) => <option key={s} value={s}>{s}</option>)}
          <option value={ADD}>+ Add new category…</option>
        </select>
      )}
    </div>
  );
}


export function PartsPicker({ parts, inventory, onChange }) {
  const update = (idx, patch) => {
    const next = parts.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const add = () => onChange([...parts, { item_id: "", item_name: "", qty: 1 }]);
  const remove = (idx) => onChange(parts.filter((_, i) => i !== idx));
  return (
    <div className="rounded-md border border-zinc-200 p-3 bg-zinc-50/40">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-600">
          Parts from inventory (optional)
        </div>
        <Button type="button" size="sm" className="bg-ink hover:bg-black" onClick={add} data-testid="add-part-row" disabled={inventory.length === 0}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add part
        </Button>
      </div>
      {parts.length === 0 && (
        <div className="text-[12px] text-zinc-500">
          {inventory.length === 0
            ? "No inventory items on this yacht yet."
            : "No parts attached. Click \"Add part\" to consume from inventory."}
        </div>
      )}
      <div className="space-y-2">
        {parts.map((p, i) => {
          const inv = inventory.find((x) => x.id === p.item_id);
          const inStock = inv?.quantity ?? 0;
          const tooMany = inv && p.qty > inStock;
          return (
            <div key={i} className="flex items-end gap-2" data-testid={`part-row-${i}`}>
              <div className="flex-1">
                <Field label={i === 0 ? "Item" : null}>
                  <select
                    className="h-9 w-full rounded-md border border-input px-3 text-sm"
                    value={p.item_id}
                    onChange={(e) => {
                      const item = inventory.find((x) => x.id === e.target.value);
                      update(i, { item_id: e.target.value, item_name: item?.name || "" });
                    }}
                    data-testid={`part-item-${i}`}
                  >
                    <option value="">— pick an item —</option>
                    {inventory.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name} · {it.quantity ?? 0} in stock
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="w-28">
                <Field label={i === 0 ? "Qty" : null}>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={p.qty}
                    onChange={(e) => update(i, { qty: e.target.value === "" ? "" : Number(e.target.value) })}
                    data-testid={`part-qty-${i}`}
                    className={tooMany ? "border-red-400" : ""}
                  />
                </Field>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => remove(i)} className="text-red-600" data-testid={`part-rm-${i}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Photo picker ───────────────────────────────────────────────────────
 * Thin wrapper around FileUploader that keeps a list of storage_paths in
 * the parent's form state and renders thumbnails for already-uploaded
 * shots. Used by the creator for `before_photos`. */
export function BeforePhotosPicker({ photos, yacht_id, onChange, onUploadPath }) {
  const [markupPath, setMarkupPath] = useState(null);
  return (
    <div className="rounded-md border border-zinc-200 p-3 bg-zinc-50/40">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-600">
          Before Media (optional)
        </div>
        <FileUploader
          yacht_id={yacht_id}
          folder="maintenance/before"
          accept="image/*,video/*"
          enableVideo
          multiple
          label="Add media"
          testid="before-photo-upload"
          onUploaded={(path) => { onChange([...photos, path]); onUploadPath?.(path); }}
        />
      </div>
      {photos.length === 0 ? (
        <div className="text-[12px] text-zinc-500">Document what you see before work starts.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((p, i) => (
            <div key={i} className="relative group">
              {isVideoPath(p) ? (
                <VideoThumb path={p} className="w-full aspect-square rounded border border-zinc-200" />
              ) : (
                <AuthImage path={p} className="w-full aspect-square object-cover rounded border border-zinc-200" />
              )}
              <button
                type="button"
                onClick={() => onChange(photos.filter((_, idx) => idx !== i))}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
              {!isVideoPath(p) && (
                <button
                  type="button"
                  onClick={() => setMarkupPath(p)}
                  className="absolute top-1 left-1 bg-black/60 hover:bg-black text-white rounded-full p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  title="Draw an arrow or circle on this photo"
                  data-testid={`before-photo-markup-${i}`}
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {markupPath && (
        <PhotoMarkup
          path={markupPath}
          yachtId={yacht_id}
          folder="maintenance/before"
          onSaved={(np) => { onChange([...photos, np]); onUploadPath?.(np); }}
          onClose={() => setMarkupPath(null)}
        />
      )}
    </div>
  );
}

export function ModalTab({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative px-4 py-2 text-sm transition-colors ${
        active ? "text-ink" : "text-zinc-500 hover:text-zinc-700"
      }`}
    >
      {children}
      {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-ink" />}
    </button>
  );
}

export function PhotoBucket({ title, paths, canAdd, yacht_id, folder, onUploaded, testid, helper, onDelete, canDelete }) {
  // Lightbox state — `null` (closed) or { paths, index } pointing at the
  // bucket's own list so prev/next cycles only through siblings of the
  // clicked thumbnail.
  const [lightbox, setLightbox] = useState(null);
  // Per-item delete confirmation: holds the path pending deletion.
  const [confirmPath, setConfirmPath] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Markup editor — holds the photo path being annotated.
  const [markupPath, setMarkupPath] = useState(null);
  const showDelete = !!(canDelete && onDelete);

  const doDelete = async () => {
    if (!confirmPath) return;
    setDeleting(true);
    try {
      await onDelete(confirmPath);
      setConfirmPath(null);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-4 rounded-md border border-zinc-200 p-3 bg-zinc-50/40">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-600">
          {title} ({paths.length})
        </div>
        {canAdd && (
          <FileUploader
            yacht_id={yacht_id}
            folder={folder}
            accept="image/*,video/*"
            enableVideo
            multiple
            label={<><ImagePlus className="h-3.5 w-3.5 mr-1.5 inline" />Add</>}
            testid={`${testid}-photo-upload`}
            onUploaded={(path) => onUploaded(path)}
          />
        )}
      </div>
      {helper && <div className="text-[12px] text-emerald-700 mb-2">{helper}</div>}
      {paths.length === 0 ? (
        <div className="text-[12px] text-zinc-500">No {title.toLowerCase()} yet.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {paths.map((p, i) => (
            <div key={i} className="relative group">
              {isVideoPath(p) ? (
                <VideoThumb
                  path={p}
                  className="w-full aspect-square rounded border border-zinc-200"
                  testid={`${testid}-photo-${i}`}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setLightbox({ paths: paths.filter((x) => !isVideoPath(x)), index: paths.filter((x) => !isVideoPath(x)).indexOf(p) })}
                  className="block w-full aspect-square overflow-hidden rounded border border-zinc-200 hover:border-zinc-400 transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-400"
                  aria-label={`Open ${title.toLowerCase()} item ${i + 1}`}
                  data-testid={`${testid}-photo-${i}`}
                >
                  <AuthImage path={p} className="w-full h-full object-cover" />
                </button>
              )}
              {showDelete && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setConfirmPath(p); }}
                  className="absolute top-1 right-1 z-10 grid place-items-center bg-black/60 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  aria-label={`Delete ${title.toLowerCase()} item ${i + 1}`}
                  data-testid={`${testid}-photo-delete-${i}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
              {canAdd && !isVideoPath(p) && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setMarkupPath(p); }}
                  className="absolute top-1 left-1 z-10 grid place-items-center bg-black/60 hover:bg-black text-white rounded-full p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  title="Draw an arrow or circle on this photo"
                  data-testid={`${testid}-photo-markup-${i}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <ImageLightbox state={lightbox} onClose={() => setLightbox(null)} />
      {markupPath && (
        <PhotoMarkup
          path={markupPath}
          yachtId={yacht_id}
          folder={folder}
          onSaved={(np) => onUploaded(np)}
          onClose={() => setMarkupPath(null)}
        />
      )}

      <AlertDialog open={!!confirmPath} onOpenChange={(o) => { if (!o && !deleting) setConfirmPath(null); }}>
        <AlertDialogContent data-testid={`${testid}-delete-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this media?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the file from storage and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid={`${testid}-delete-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid={`${testid}-delete-confirm-btn`}
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function Row({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className="text-sm text-zinc-800">{value || "—"}</div>
    </div>
  );
}

export function PriorityPill({ v }) {
  const m = { low: "text-zinc-600", medium: "text-blue-700", high: "text-amber-700", critical: "text-red-700" };
  return <span className={`text-xs ${m[v] || ""}`}>{v}</span>;
}

/**
 * Inline editable cost. Renders the value as text by default; clicking it
 * (or the pencil/Set button when empty) reveals a number input + Save.
 * Used in the maintenance JobDetailModal so the assigned provider can post
 * a final price without senior crew having to open the full edit dialog.
 */
export function CostInput({ value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  useEffect(() => { setDraft(value == null ? "" : String(value)); }, [value]);
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-sm text-zinc-800 hover:underline decoration-dotted text-left"
        data-testid={`${testid}-display`}
      >
        {value != null ? value.toFixed(2) : <span className="text-zinc-400">— Set cost</span>}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <Input
        type="number"
        step="0.01"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { onSave(draft); setEditing(false); }
          if (e.key === "Escape") { setEditing(false); setDraft(value == null ? "" : String(value)); }
        }}
        className="no-spinner h-7 w-28 text-sm"
        data-testid={testid}
      />
      <Button
        size="sm"
        className="h-7 bg-ink hover:bg-black"
        onClick={() => { onSave(draft); setEditing(false); }}
        data-testid={`${testid}-save`}
      >
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-7"
        onClick={() => { setEditing(false); setDraft(value == null ? "" : String(value)); }}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/**
 * Documents attached to a maintenance job — quotations, invoices, reports.
 * Mirrors PhotoBucket but accepts PDFs/images and groups items by kind.
 * Uses the same FileUploader so the storage path round-trip is identical.
 */
export function DocumentsBucket({ documents, canAdd, canRemoveAny, userId, yacht_id, onUploaded, onRemove }) {
  const [kind, setKind] = useState("quotation");
  return (
    <div className="mt-4 rounded-md border border-zinc-200 p-3 bg-zinc-50/40" data-testid="documents-bucket">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-600">
          Documents ({documents.length})
        </div>
        {canAdd && (
          <div className="flex items-center gap-2">
            <select
              className="h-8 rounded-md border border-input px-2 text-[12px] bg-white"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              data-testid="documents-kind"
            >
              <option value="quotation">Quotation</option>
              <option value="invoice">Invoice</option>
              <option value="payment">Payment</option>
              <option value="report">Report</option>
              <option value="other">Other</option>
            </select>
            <FileUploader
              yacht_id={yacht_id}
              folder="maintenance/documents"
              accept="application/pdf,image/*"
              label={<><Plus className="h-3.5 w-3.5 mr-1.5 inline" />Attach</>}
              testid="documents-upload"
              onUploaded={(path, record) => onUploaded(path, record?.original_filename || "document", kind)}
            />
          </div>
        )}
      </div>
      {documents.length === 0 ? (
        <div className="text-[12px] text-zinc-500">
          No documents yet. Attach quotations before work starts and invoices when complete.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white">
          {documents.map((d) => (
            <li key={d.id} className="px-3 py-2 flex items-center gap-3 min-w-0" data-testid={`document-${d.id}`}>
              <span className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                d.kind === "quotation" ? "bg-amber-100 text-amber-800"
                : d.kind === "invoice" ? "bg-emerald-100 text-emerald-800"
                : d.kind === "payment" ? "bg-lime-100 text-lime-800"
                : d.kind === "report" ? "bg-blue-100 text-blue-800"
                : "bg-zinc-100 text-zinc-700"
              }`}>
                {d.kind}
              </span>
              <a
                href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/files/${d.file_path}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-zinc-800 hover:underline truncate flex-1 min-w-0"
                title={d.name}
                data-testid={`document-${d.id}-link`}
              >
                {d.name}
              </a>
              <span className="text-[11px] text-zinc-500 hidden sm:inline whitespace-nowrap shrink-0">
                by {d.uploaded_by_name} · {(d.uploaded_at || "").slice(0, 10)}
              </span>
              {(canRemoveAny || d.uploaded_by === userId) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(d.id)}
                  className="text-red-600"
                  data-testid={`document-${d.id}-remove`}
                  title="Remove document"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* VAT breakdown shown under a quote's scope list when the quote carries a
 * vat_percent — Subtotal (sum of item prices) + VAT amount. The quote's
 * `amount` is the end price INCLUDING VAT. */
export function VatBreakdown({ items = [], amount, currency, vatPercent, testid = "vat-breakdown" }) {
  if (vatPercent == null) return null;
  const sub = items.reduce((s, it) => s + (Number(it.price) || 0), 0);
  const vat = Math.round((Number(amount || 0) - sub) * 100) / 100;
  const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="mt-1 pt-1 border-t border-zinc-200/70 space-y-0.5 text-xs" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-zinc-500">Subtotal</span>
        <span className="text-zinc-700">{currency} {money(sub)}</span>
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-zinc-500">VAT {vatPercent}%</span>
        <span className="text-zinc-700">{currency} {money(vat)}</span>
      </div>
    </div>
  );
}

/* "Show invoice" shortcut on confirmed tasks — jumps to the invoice detail
   window in the Invoices menu (yacht or supplier side). Disabled with a
   "No invoice yet" hint when the task has no linked invoice. */
export function ShowInvoiceButton({ invoiceId, supplier = false, testid }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      disabled={!invoiceId}
      title={invoiceId ? "Open the invoice for this task" : "No invoice yet"}
      onClick={(e) => {
        e.stopPropagation();
        if (invoiceId) navigate(`${supplier ? "/app/supplier/invoices" : "/app/invoices"}?open=${invoiceId}`);
      }}
      className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2 py-1 text-[11px] font-medium text-zinc-700 transition-colors hover:bg-zinc-100 disabled:opacity-45 disabled:cursor-not-allowed"
      data-testid={testid}
    >
      <Receipt className="h-3 w-3" />
      {invoiceId ? "Show invoice" : "No invoice yet"}
    </button>
  );
}

/* "Show task" shortcut on invoices that came from a confirmed task — jumps to
   the task detail window in the Tasks menu (yacht or supplier side). Hidden
   when the invoice has no linked task. */
export function ShowTaskButton({ maintId, supplier = false, testid }) {
  const navigate = useNavigate();
  if (!maintId) return null;
  return (
    <button
      type="button"
      title="Open the task for this invoice"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`${supplier ? "/app/supplier/tasks" : "/app/tasks"}?open=${maintId}`);
      }}
      className="inline-flex items-center gap-1 rounded-md border border-zinc-300 bg-white px-2 py-1 text-[11px] font-medium text-zinc-700 transition-colors hover:bg-zinc-100"
      data-testid={testid}
    >
      <Wrench className="h-3 w-3" />
      Show task
    </button>
  );
}
